import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";

const hexToRgba = (hex, alpha = 0.2) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getEmptyChartData = (labels = []) => ({
  labels: labels.length ? labels : [new Date()],
  datasets: [
    {
      label: "No Data",
      data: [],
      borderColor: "rgba(0,0,0,0.2)",
      borderWidth: 1,
      borderDash: [5, 5],
      pointRadius: 0,
    },
  ],
});



const calculateXTicks = (labels, step, unit) => {
  if (!labels?.length) return 0;

  const start = new Date(labels[0]);
  const end = new Date(labels.at(-1));
  const current = new Date(start);

  let count = 0;
  while (current <= end) {
    count++;
    unit === "hour"
      ? current.setHours(current.getHours() + step)
      : current.setMinutes(current.getMinutes() + step);
  }

  return count;
};


const LineChart = ({
  dateTimesLive,
  dataArrLive,
  getRandomColor,
  chartChannel,
  selectedTabIndex,
  hour,
  minute,
  oneDayDataTs,
  lineStyle,
  isCurveLine,
  isSteppedLine,
  minY,
  maxY,
  width,
  height,
  xInterval,
  yInterval,
  channelsGroup,
  allChannelGroups,
}) => {
  const chartRef = useRef(null);

  const [linechartData, setLineChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState({});
  const [minChartWidth, setMinChartWidth] = useState("500px");
  const [minChartHeight, setMinChartHeight] = useState("300px");



  /* ───────────────────── DATA BUILD ───────────────────── */

  useEffect(() => {
    if (!Array.isArray(dataArrLive)) return;

    const timeArr =
      dateTimesLive
        ?.map(t => {
          const d = new Date(t);
          return isNaN(d) ? null : d.getTime();
        })
        .filter(Boolean) || [];

    const now = new Date();
    const start = new Date(now);
    start.setHours(now.getHours() - hour);
    start.setMinutes(now.getMinutes() - minute);

    const filteredTimeArr =
      oneDayDataTs?.filter(
        t => t >= start.toISOString() && t <= now.toISOString()
      ) || [];

    const availableChannels = dataArrLive.filter(
      ds => Array.isArray(ds.data) && ds.data.at(-1) !== null
    );

    let matchedDatasets = availableChannels;

    if (channelsGroup) {
      const group = allChannelGroups?.find(
        g => g.groupName === channelsGroup
      );
      if (group?.channels?.length) {
        matchedDatasets = group.channels
          .map(label =>
            availableChannels.find(ds => ds.label === label)
          )
          .filter(Boolean);
      }
    }

    if (chartChannel?.length) {
      matchedDatasets = chartChannel
        .map(label =>
          availableChannels.find(ds => ds.label === label)
        )
        .filter(Boolean);
    }


    const convertedTimeArr = Array.isArray(filteredTimeArr) && filteredTimeArr.length > 0 ? filteredTimeArr.map((label) => new Date(label)) : timeArr.map((label) => new Date(label));


    const convertedTimeArrLength = convertedTimeArr.length
    const filteredMatchData = matchedDatasets.map((dataset) => {
      return {
        ...dataset,
        data: Array.isArray(dataset.data)
          ? dataset.data.slice(-convertedTimeArrLength)
          : []
      }
    })


    const datasets = filteredMatchData.map(ds => {
      const color = getRandomColor();
      return {
        label: ds.label,
        data: ds.data,
        borderColor: color,
        backgroundColor:
          lineStyle?.toLowerCase() === "fill"
            ? hexToRgba(color, 0.2)
            : undefined,
        fill: lineStyle?.toLowerCase() === "fill",
        borderDash:
          lineStyle?.toLowerCase() === "dash" ? [10, 4] : [],
        borderWidth: 1,
        pointRadius: 2,
        tension: isCurveLine ? 0.3 : 0,
        stepped: !!isSteppedLine,
      };
    });

    if (!datasets.length) {
      const emptyLabels = filteredTimeArr.length ? filteredTimeArr : timeArr;
      setLineChartData(getEmptyChartData(emptyLabels));
      return;
    }

    setLineChartData({
      labels: filteredTimeArr.length ? filteredTimeArr : timeArr,
      datasets,
    });

  }, [
    dateTimesLive,
    dataArrLive,
    chartChannel,
    selectedTabIndex,
    hour,
    minute,
    lineStyle,
    isCurveLine,
    isSteppedLine,
  ]);

  /* ───────────────────── AXIS + SCROLL LOGIC ───────────────────── */

  useEffect(() => {
    const LABEL_Y_PX = 20;
    const LABEL_X_PX = 40;

    /* ---------- Y AXIS HEIGHT ---------- */

    if (yInterval && dataArrLive?.length) {
      let yMin = minY;
      let yMax = maxY;

      if (yMin === undefined || yMax === undefined) {
        let dMin = Infinity;
        let dMax = -Infinity;
        dataArrLive.forEach(ds =>
          ds.data.forEach(v => {
            if (v != null) {
              dMin = Math.min(dMin, v);
              dMax = Math.max(dMax, v);
            }
          })
        );

        if (yMin === undefined)
          yMin = Math.floor(dMin / yInterval) * yInterval;
        if (yMax === undefined)
          yMax = Math.ceil(dMax / yInterval) * yInterval;
      }


      const yTickCount = Math.floor((yMax - yMin) / yInterval) + 1;
      const newHeight = Number(height.slice(0, height.length - 2));

      if (yTickCount * LABEL_Y_PX <= newHeight) {
        setMinChartHeight(height);

      } else {
        console.log("A:", yTickCount * LABEL_Y_PX);
        setMinChartHeight(`${yTickCount * LABEL_Y_PX}px`);
      }

    } else {
      setMinChartHeight(height || "300px");
    }

    /* ---------- X AXIS WIDTH ---------- */

    if (xInterval && linechartData?.labels?.length) {
      const step = Number(xInterval.slice(0, -1));
      const unit =
        xInterval.slice(-1).toUpperCase() === "H"
          ? "hour"
          : "minute";

      const xTickCount = calculateXTicks(
        linechartData.labels,
        step,
        unit
      );

      setMinChartWidth(`${xTickCount * LABEL_X_PX}px`);
    }

    /* ---------- CHART OPTIONS ---------- */

    setChartOptions({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "time",
          time: {
            unit:
              xInterval?.slice(-1).toUpperCase() === "H"
                ? "hour"
                : "minute",
            stepSize: xInterval
              ? Number(xInterval.slice(0, -1))
              : 1,
          },
          ticks: {
            autoSkip: false,
            maxRotation: 25,
          },
        },
        y: {
          min: minY,
          max: maxY,
          ticks: {
            stepSize: yInterval,
            autoSkip: false,
            callback: v => Number(v).toFixed(0),
          },
        },
      },
      plugins: {
        zoom: {
          pan: {
            enabled: true,
            mode: "y",
            modifierKey: "ctrl",
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },

            mode: "y",
          },
        },
        legend: {
          align: "left",
          labels: {
            boxWidth: 6,
            font: { size: 10 },
          },
        },
      },
    });
  }, [xInterval, yInterval, minY, maxY, linechartData, height]);

  /* ───────────────────── RENDER ───────────────────── */

  return (
    <div
      style={{
        width: "100%",
        height: height || "300px",
        overflow: "hidden",
        border: "1px solid #ddd",
      }}
    >
      <div style={{ width: "100%", height: height ? height : "100%", overflow: "auto" }}>
        <div
          style={{
            minWidth: minChartWidth,
            minHeight: minChartHeight,
            position: "relative",
          }}
        >
          {linechartData && (
            <Line
              ref={chartRef}
              data={linechartData}
              options={chartOptions}
            />
          )}
        </div>
      </div>
    </div>
  );
};




const NewLineChart = ({
  dateTimesLive,
  dataArrLive,
  chartChannel,
  getRandomColor,
  lineStyle,
  isCurveLine,
  isSteppedLine,
  minY,
  maxY,
  height,
  width,
  xInterval,
  yInterval,
  endMinutes,
  endHours,
  oneDayDataTs
}) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState();
  const [chartOptions, setChartOptions] = useState();
  const [minChartWidth, setMinChartWidth] = useState("500px");
  const [minChartHeight, setMinChartHeight] = useState("300px");

  useEffect(() => {

    if (!Array.isArray(dataArrLive)) return;

    const timeArr = dateTimesLive?.map(item => {
      const d = new Date(item);
      return isNaN(d) ? null : d.getTime();
    }).filter(Boolean) || [];

    const now = new Date();
    const end = new Date(now);
    end.setHours(now.getHours() - endHours);
    end.setMinutes(now.getMinutes() - endMinutes);

    const startISO = isNaN(end) ? new Date().toISOString() : end.toISOString();
    const endISO = now.toISOString();

    const filteredTimeArr =
      oneDayDataTs?.filter(t => t >= startISO && t <= endISO) || [];

    const labels = dateTimesLive.map(t => new Date(t));

    const availableChannels = dataArrLive.filter(
      ds => Array.isArray(ds.data) && ds.data.at(-1) !== null
    );

    const matched =
      chartChannel?.length > 0
        ? chartChannel.map(l =>
          availableChannels.find(d => d.label === l)
        )
        : availableChannels;

    const datasets = [];


    const convertedTimeArr = Array.isArray(filteredTimeArr) && filteredTimeArr.length > 0 ? filteredTimeArr.map((label) => new Date(label)) : timeArr.map((label) => new Date(label));


    const convertedTimeArrLength = convertedTimeArr.length
    const filteredMatchData = matched.map((dataset) => {
      return {
        ...dataset,
        data: Array.isArray(dataset.data)
          ? dataset.data.slice(-convertedTimeArrLength)
          : []
      }
    })


    filteredMatchData.forEach(ds => {
      const color = getRandomColor();

      datasets.push({
        label: ds.label,
        data: ds.data,
        borderColor: color,
        borderWidth: lineStyle === "dash" ? 2 : 1,
        borderDash: lineStyle === "dash" ? [10, 4] : [],
        fill: lineStyle === "fill",
        backgroundColor:
          lineStyle === "fill" ? hexToRgba(color, 0.2) : undefined,
        tension: isCurveLine ? 0.3 : 0,
        stepped: isSteppedLine,
        pointRadius: 0,
      })
    });

    if (!datasets.length) {
      const emptyLabels = filteredTimeArr.length ? filteredTimeArr : timeArr;
      setChartData(getEmptyChartData(emptyLabels));
      return;
    }

    setChartData({ labels: filteredTimeArr.length ? filteredTimeArr : timeArr, datasets });
  }, [
    dateTimesLive,
    dataArrLive,
    chartChannel,
    lineStyle,
    isCurveLine,
    isSteppedLine,
    endHours,
    endMinutes
  ]);


  useEffect(() => {
    const LABEL_Y_PX = 20;
    const LABEL_X_PX = 40;

    /* ---------- Y AXIS HEIGHT ---------- */

    if (yInterval && dataArrLive?.length) {
      let yMin = minY;
      let yMax = maxY;

      if (yMin === undefined || yMax === undefined) {
        let dMin = Infinity;
        let dMax = -Infinity;

        dataArrLive.forEach(ds =>
          ds.data.forEach(v => {
            if (v != null) {
              dMin = Math.min(dMin, v);
              dMax = Math.max(dMax, v);
            }
          })
        );

        if (yMin === undefined)
          yMin = Math.floor(dMin / yInterval) * yInterval;
        if (yMax === undefined)
          yMax = Math.ceil(dMax / yInterval) * yInterval;
      }

      const yTickCount = Math.floor((yMax - yMin) / yInterval) + 1;
      const newHeight = Number(height.slice(0, height.length - 2));

      if (yTickCount * LABEL_Y_PX <= newHeight) {
        setMinChartHeight(height);

      } else {
        setMinChartHeight(`${yTickCount * LABEL_Y_PX}px`);
      }

    } else {
      setMinChartHeight(height || "300px");
    }

    /* ---------- X AXIS WIDTH ---------- */

    if (xInterval && chartData?.labels?.length) {
      const step = Number(xInterval.slice(0, -1));
      const unit =
        xInterval.slice(-1).toUpperCase() === "H"
          ? "hour"
          : "minute";

      const xTickCount = calculateXTicks(
        chartData.labels,
        step,
        unit
      );

      setMinChartWidth(`${xTickCount * LABEL_X_PX}px`);
    }

    /* ---------- CHART OPTIONS ---------- */

    setChartOptions({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "time",
          time: {
            unit:
              xInterval?.slice(-1).toUpperCase() === "H"
                ? "hour"
                : "minute",
            stepSize: xInterval
              ? Number(xInterval.slice(0, -1))
              : 1,
          },
          ticks: {
            autoSkip: false,
            maxRotation: 25,
          },
        },
        y: {
          min: minY,
          max: maxY,
          ticks: {
            stepSize: yInterval,
            autoSkip: false,
            callback: v => Number(v).toFixed(0),
          },
        },
      },
      plugins: {
        zoom: {
          pan: {
            enabled: true,
            mode: "y",
            modifierKey: "ctrl",
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },

            mode: "y",
          },
        },
        legend: {
          align: "left",
          labels: {
            boxWidth: 6,
            font: { size: 10 },
          },
        },
      },
    });
  }, [xInterval, yInterval, minY, maxY, chartData, height]);







  return (

    <div
      style={{
        width: "100%",
        height: height ? height : "300px",
        overflow: "hidden",
        border: "1px solid #ddd",
      }}
    >
      {/* 🟡 SCROLL CONTAINER */}
      <div
        style={{
          width: "100%",
          height: height ? height : "315px",
          overflow: "auto",
        }}
      >
        {/* 🟢 CHART CONTAINER */}
        <div
          style={{
            minWidth: minChartWidth,
            height: minChartHeight,
            position: "relative",
          }}
        >
          {chartData && (
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export { LineChart, NewLineChart };